import styled from "styled-components";

import {
  Box,
  CustomCard,
  TitleComponent,
  Avatar,
  useToggle,
  NoData,
} from "ui-components";
import { useMemo, useState } from "react";
import { useQueries, useQuery } from "react-query";
import { getTopMegaDeal } from "ui-io/queries/get-top-mega-deal";
import { MegaDealCompanyT } from "ui-io/company-io";
import { getTopIndustry } from "ui-io/queries/get-top-industry";
import { Colors } from "@blueprintjs/core";
import { formatLargeNumber } from "ui-lib";
import CompanyDetails from "../company/details";
import { DealByIndustryT } from "ui-io/deal-io";
import {useWindowWidth} from "ui-lib";

type Props = {
  year?: string;
};
function TopIndustries(props: Props) {
  const windowWidth = useWindowWidth();
  const { year } = props;
  const [connectionMegaDeal, connectionTopIndustry] = useQueries([
    getTopMegaDeal({ limit: 5, year: year }),
    getTopIndustry({ limit: 5 }),
  ]);
  const parseData = (connectionData: any) => {
    if (!connectionData?.length) {
      return [];
    }
    return connectionData.map((company: MegaDealCompanyT) => {
      return {
        companyID: company.companyID,
        name: company.name,
        url: company.url,
        totalFunding: company.totalFunding,
      };
    });
  };
  const megaDealData: MegaDealCompanyT[] = useMemo(() => {
    return parseData(connectionMegaDeal.data);
  }, [connectionMegaDeal.data]);

  const topIndustryData: DealByIndustryT[] = useMemo(() => {
    return connectionTopIndustry.data || [];
  }, [connectionTopIndustry.data]);
  return (
    <Box
      flexDir="row"
      gap="xxlarge"
      mt={"large"}
      wrap={windowWidth > 1480 ? false : true}
      minWidth={"1"}
    >
      <Box flexDir="col" style={{ width: windowWidth > 1480 ? "32%" : "47%" }}>
        <CustomCardContainer marginTop="5px">
          <TitleComponent marginTop={"10px"} className="ml-sm">
            Top 5 Mega Deals
          </TitleComponent>
          <GetDeals dealsData={megaDealData} />
        </CustomCardContainer>
      </Box>
      <Box flexDir="col" style={{ width: windowWidth > 1480 ? "32%" : "47%" }}>
        <CustomCardContainer marginTop="5px">
          <TitleComponent marginTop={"10px"} className="ml-sm">
            Top 5 Industries
          </TitleComponent>
          <GetDealsByIndustry dealsData={topIndustryData} />
        </CustomCardContainer>
      </Box>
      <Box flexDir="col" style={{ width: windowWidth > 1480 ? "32%" : "47%" }}>
        <CustomCardContainer marginTop="5px">
          <TitleComponent marginTop={"10px"} className="ml-sm">
            Top 5 Investors
          </TitleComponent>
          <GetDeals dealsData={[]} />
        </CustomCardContainer>
      </Box>
    </Box>
  );
}

export default TopIndustries;

const GetDealsByIndustry = ({
  dealsData = [],
}: {
  dealsData: DealByIndustryT[];
}) => {
  if (!dealsData?.length) {
    return (
      <div>
        <NoData title="No Data Found" icon="warning-sign" />
      </div>
    );
  }
  return (
    <ul className="pl-none">
      {dealsData?.length &&
        dealsData.map((deal: DealByIndustryT, index) => (
          <DealsItem key={index}>
            <Avatar
              email={"yantrix@gmail.com"}
              onlyAvatar={true}
              displayName={deal.industry}
              isCircle={true}
              isCurrentUser={false}
              isLoading={false}
              size="30px"
            />

            <DealsName>{deal.industry}</DealsName>
            <span className="mr-lg fw-700">
              £{formatLargeNumber(deal.totalFunding)}
            </span>
          </DealsItem>
        ))}
    </ul>
  );
};

const GetDeals = ({
  dealsData = [],
  viewMore = true,
}: {
  dealsData: MegaDealCompanyT[];
  viewMore?: boolean;
}) => {
  const [currentCompany, setCurrentComapny] = useState<any>();
  const [isCompanyDetail, setToggleCompanyDetail] = useToggle(false);

  const handleCompanyDetails = (companyID: string) => {
    const currentCompany = dealsData.find(
      (company) => company.companyID === companyID,
    );
    if (
      currentCompany &&
      Object.keys(currentCompany).length &&
      currentCompany.name
    ) {
      setCurrentComapny({
        ...currentCompany,
        companyName: currentCompany.name,
      });
      setToggleCompanyDetail();
    }
  };
  if (!dealsData?.length) {
    return (
      <div>
        <NoData title="No Data Found" icon="warning-sign" />
      </div>
    );
  }
  return (
    <ul className="pl-none">
      <CompanyDetails
        isCompanyDetail={isCompanyDetail}
        setToggleCompanyDetail={setToggleCompanyDetail}
        currentCompany={currentCompany}
      />
      {dealsData?.length &&
        dealsData.map((deal: MegaDealCompanyT) => (
          <DealsItem key={deal.companyID}>
            <Avatar
              email={"yantrix@gmail.com"}
              profileUrl={deal.url?.logo || ""}
              onlyAvatar={true}
              displayName={deal.name}
              isCircle={true}
              isCurrentUser={false}
              isLoading={false}
              size="30px"
            />

            <DealsName>{deal.name}</DealsName>
            <span className="mr-lg fw-700">
              £{formatLargeNumber(deal.totalFunding)}
            </span>
            {viewMore ? (
              <ViewMore
                onClick={(e) => handleCompanyDetails(deal.companyID)}
                className="ml-lg"
              >
                View More
              </ViewMore>
            ) : null}
          </DealsItem>
        ))}
    </ul>
  );
};
const CustomCardContainer = styled(CustomCard)`
  width: 100%;
  min-height: 350px;
  flex: 1;
  align-self: flex-start;
  overflow: "hidden";
`;

const DealsItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-radius: 10px;

  &:hover {
    background-color: ${({ theme: { darkMode } }) =>
      darkMode ? Colors.DARK_GRAY4 : Colors.LIGHT_GRAY5};
  }
`;

const DealsName = styled.span`
  flex: 1;
  margin-left: 10px;
  font-weight: 500;
`;

const ViewMore = styled.a`
  text-decoration: none;
  color: ${({ theme: { darkMode } }) =>
    darkMode ? Colors.GRAY4 : Colors.DARK_GRAY4} !important;
  font-size: 0.9rem;
  &:hover {
    color: ${({ theme: { darkMode } }) =>
      darkMode ? Colors.LIGHT_GRAY3 : Colors.DARK_GRAY3} !important;
  }
`;
